// Downscale large phone photos in the browser before upload — faster scans, lower AI cost.
function loadImg(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

const RAD = (deg) => (deg * Math.PI) / 180;

function rotatedSize(width, height, rotation) {
  const rad = RAD(rotation);
  return {
    width: Math.abs(Math.cos(rad) * width) + Math.abs(Math.sin(rad) * height),
    height: Math.abs(Math.sin(rad) * width) + Math.abs(Math.cos(rad) * height),
  };
}

// Crop an image (from an object URL) to the given pixel rect, honoring rotation and an
// optional auto-brighten pass, and return a JPEG File.
export async function getCroppedFile(imageSrc, cropPixels, name = "card.jpg", opts = {}) {
  const { rotation = 0, enhance = false, quality = 0.9 } = opts;
  const img = await loadImg(imageSrc);

  const bBox = rotatedSize(img.width, img.height, rotation);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bBox.width);
  canvas.height = Math.round(bBox.height);
  const ctx = canvas.getContext("2d");
  if (enhance) ctx.filter = "brightness(1.2) contrast(1.12) saturate(1.05)";
  ctx.translate(bBox.width / 2, bBox.height / 2);
  ctx.rotate(RAD(rotation));
  ctx.translate(-img.width / 2, -img.height / 2);
  ctx.drawImage(img, 0, 0);
  ctx.filter = "none";

  const data = ctx.getImageData(
    Math.round(cropPixels.x),
    Math.round(cropPixels.y),
    Math.round(cropPixels.width),
    Math.round(cropPixels.height)
  );
  canvas.width = Math.max(1, Math.round(cropPixels.width));
  canvas.height = Math.max(1, Math.round(cropPixels.height));
  ctx.putImageData(data, 0, 0);

  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
  const base = (name || "card").replace(/\.[^.]+$/, "");
  return new File([blob], `${base}.jpg`, { type: "image/jpeg" });
}

export async function downscaleImage(file, maxDim = 1600, quality = 0.85) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  let url;
  try {
    let width, height, source;
    const bitmap = await createImageBitmap(file).catch(() => null);
    if (bitmap) {
      width = bitmap.width;
      height = bitmap.height;
      source = bitmap;
    } else {
      url = URL.createObjectURL(file);
      const img = await loadImg(url);
      width = img.naturalWidth;
      height = img.naturalHeight;
      source = img;
    }
    if (!width || !height) return file;
    const scale = Math.min(1, maxDim / Math.max(width, height));
    // Already small enough — don't re-encode.
    if (scale === 1 && file.size < 1500000) return file;
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(source, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (bitmap && bitmap.close) bitmap.close();
    if (!blob || blob.size >= file.size) return file;
    const base = (file.name || "card").replace(/\.[^.]+$/, "");
    return new File([blob], `${base}.jpg`, { type: "image/jpeg" });
  } catch {
    return file;
  } finally {
    if (url) URL.revokeObjectURL(url);
  }
}
